import { CloudActions, CloudErrors, FlowjoCommons } from '@portal/portal-core';
import flag from 'node-env-flag';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
import type { RootState } from 'reducers';
import { useAppDispatch } from 'store/hooks';

import CloudAdmin from 'components/cloud-admin';
import AlertUi, { type AlertStore } from 'components/alert-ui';
import ChangePassword from 'components/auth/change-password';
import CreateUser from 'components/auth/create-user';
import EmailNotificationBase from 'components/auth/email-notification';
import ForgotPassword from 'components/auth/forgot-password';
import PasswordReset from 'components/auth/password-reset';
import SignIn from 'components/auth/sign-in';
import EULAModal from 'components/eula';
import Loading from 'components/loading';
import ManageDevices from 'components/manage-devices';
import { FjFlexContainer, FjTooltip } from 'components/primitives';
import ProductTrialBase from 'components/product-trial';
import Settings from 'components/settings';
import PortPersonalData from 'components/settings/port-personal-data';
import UserAccountBase from 'components/user-account';
import { AuthClientActions, AuthClientSelectors, AuthClientUtils } from 'ducks/auth-client';
import { UserClientActions } from 'ducks/user-client';
import SuperAdmin from 'components/super-admin';
import {
  APPLICATION_NAME,
  chinaFlag,
  compareValueToLocalStorage,
  getValueForUrlProperty,
  getValueInLocalStorage,
  isSuperAdmin,
  navigate,
  setValueInLocalStorage,
  urlParameterValues,
} from 'utils';

// Injected by Vite from the workspace root manifest; see src/types/build-constants.d.ts.
const dependencies = __APP_DEPENDENCIES__;
const version = __APP_VERSION__;

const { Alerts } = FlowjoCommons;

// email-notification, product-trial and user-account are untyped .js default exports
// whose inferred prop types are too narrow (message defaults to null) or empty (the
// container components read their own data). Cast at the .js boundary to keep the
// existing call shapes; any extra props are ignored at runtime.
const EmailNotification = EmailNotificationBase as React.ComponentType<{
  title?: string;
  message?: React.ReactNode;
  error?: boolean;
  footerText?: string;
  linkText?: string;
  onClickLink?: () => void;
}>;
const ProductTrial = ProductTrialBase as React.ComponentType<{ selectedProductName?: string }>;
const UserAccount = UserAccountBase as React.ComponentType<{ store?: unknown }>;

const ENABLE__GDPR_FUTURE = flag(process.env.ENABLE__GDPR_FUTURE, false);

// Mirrors the original constructor/componentDidUpdate checkAndSetCurrentUrl: keep
// localStorage 'storedRoute' in sync with window.location and return the stored value.
const syncStoredRoute = (): unknown => {
  if (!window) {
    return getValueInLocalStorage('storedRoute');
  }
  const processUrl = window.location;
  const urlMatch = compareValueToLocalStorage('storedRoute', processUrl);
  if (!urlMatch) {
    setValueInLocalStorage('storedRoute', processUrl);
  }
  return getValueInLocalStorage('storedRoute');
};

export interface RouterProps {
  configIsReady: boolean;
  authenticated: boolean;
  missingAgreements: boolean;
  fetchingAgreements: boolean;
  isVerifyingEmail: boolean;
}

export const Router = ({
  configIsReady,
  authenticated,
  missingAgreements,
  fetchingAgreements,
  isVerifyingEmail,
}: RouterProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  // AlertUi/CloudAdmin/SuperAdmin only read store.dispatch (AlertStore's minimal shape).
  // The concrete redux store's dispatch parameter (Action) is contravariantly
  // incompatible with AlertStore's (unknown), so narrow via unknown at this boundary.
  const store = useStore() as unknown as AlertStore;

  const emailVerification = useSelector((state: RootState) =>
    AuthClientSelectors.getEmailVerificationUiState(state.emailVerification),
  );
  const userId = useSelector((state: RootState) =>
    AuthClientSelectors.getUserIdState(state.emailVerification),
  );
  const emailAddress = useSelector((state: RootState) =>
    AuthClientSelectors.getEmailAddressState(state.emailVerification),
  );

  const clientAcceptInviteResult = useCallback(
    (open: boolean, success: boolean, result: unknown) =>
      dispatch(UserClientActions.clientAcceptInviteResult(open, success, result)),
    [dispatch],
  );
  const resendEmailVerificationToken = useCallback(
    (uid: unknown, email: unknown) =>
      dispatch(CloudActions.emailVerificationTokenResend(uid, email)),
    [dispatch],
  );
  const signOut = useCallback(() => dispatch(AuthClientActions.signOut()), [dispatch]);

  const params = useMemo<Record<string, string | undefined>>(() => urlParameterValues(), []);

  // currentUrl mirrors the original class instance field: initialised once (like the
  // constructor) then re-synced after each commit (like componentDidUpdate). It lives in a
  // ref so the re-sync never triggers a render — storing window.location yields a fresh value
  // each call, which as state would loop forever.
  const currentUrlRef = useRef<unknown>(undefined);
  const initedRef = useRef(false);
  if (!initedRef.current) {
    initedRef.current = true;
    currentUrlRef.current = syncStoredRoute();
  }

  // componentDidUpdate equivalent: re-sync the stored route and, on create-user success,
  // mark the invite accepted once fetchingAgreements settles.
  const prevFetchingRef = useRef(fetchingAgreements);
  useEffect(() => {
    currentUrlRef.current = syncStoredRoute();

    const isCreateUserSuccess = Object.keys(params).includes(CloudActions.CREATE_NEW_USER_SUCCESS);
    if (
      isCreateUserSuccess &&
      !fetchingAgreements &&
      fetchingAgreements !== prevFetchingRef.current
    ) {
      clientAcceptInviteResult(true, true, null);
    }
    prevFetchingRef.current = fetchingAgreements;
  });

  // The class signed out during render when a valid reset code was present; in a function
  // component that side effect must run from an effect, not during render.
  const resetConfirmation = Alerts.AlertUtils.getQueryVariable('passwordResetCode');
  const hasValidResetCode = !!(resetConfirmation && resetConfirmation.length > 16);
  useEffect(() => {
    if (hasValidResetCode) {
      signOut();
    }
  }, [hasValidResetCode, signOut]);

  const getCurrentViewString = (): string => {
    // currentUrlRef holds window.location; getValueForUrlProperty only reads the URL
    // properties Location also exposes. pathname is always a string here, so the
    // non-null assertion preserves the original .split behaviour exactly.
    const urlHashString = getValueForUrlProperty(currentUrlRef.current as URL, 'pathname');
    const urlHashesArray: string[] = urlHashString!.split('/');

    let modifiedUrlHashesArray: string[] | null;
    if (!urlHashesArray) {
      modifiedUrlHashesArray = null;
    } else if (urlHashesArray[0] === '') {
      modifiedUrlHashesArray = urlHashesArray.splice(1, 1);
    } else {
      modifiedUrlHashesArray = urlHashesArray;
    }

    return (modifiedUrlHashesArray && modifiedUrlHashesArray[0]) || '';
  };

  const getParamKeys = (): string[] => Object.keys(params);

  const isEmailVerificationPending = (): boolean =>
    getParamKeys().includes(CloudActions.EMAIL_VERIFICATION_UNVERIFIED) || isVerifyingEmail;

  const isInviteExpired = (): boolean =>
    getParamKeys().includes(CloudErrors.inviteUserErrorTypes.INVITE_EXPIRED);

  const isInviteCancelled = (): boolean =>
    getParamKeys().includes(CloudErrors.inviteUserErrorTypes.INVITE_CANCELLED);

  const renderMain = (): React.ReactNode => {
    if (!configIsReady) {
      return <Loading className={undefined} />;
    }

    const viewToRender = getCurrentViewString();

    if (viewToRender === 'version') {
      const coreLibraryDesc = 'Core Library: ';
      return (
        <div>
          <ul>
            <li>
              <FjTooltip
                Component={coreLibraryDesc + getValueInLocalStorage('portalCoreLibraryVersion')}
              >
                API Version: {getValueInLocalStorage('portalApiVersion') as React.ReactNode}
              </FjTooltip>
            </li>
            <li>
              <FjTooltip Component={coreLibraryDesc + __PORTAL_CORE_VERSION__}>
                Webapp Version: {version}
              </FjTooltip>
            </li>
          </ul>
        </div>
      );
    }

    if (viewToRender === 'dependencies') {
      const linkPrefix = 'https://www.npmjs.com/package/';
      return (
        <div>
          <ul>
            {Object.keys(dependencies).map((dep) => (
              <li key={dep}>
                <FjTooltip Component={dependencies[dep as keyof typeof dependencies]}>
                  <a href={`${linkPrefix}${dep}`}>{dep}</a>
                </FjTooltip>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (fetchingAgreements) {
      return <Loading className={undefined} />;
    }

    if (missingAgreements) {
      return <EULAModal />;
    }

    // if user is authenticated and url has passwordResetCode, then log them
    // out (handled in the effect above) before rendering Password Reset UI
    if (hasValidResetCode) {
      return <PasswordReset />;
    }

    // unauthenticated-specific routes
    if (!authenticated) {
      if (hasValidResetCode) {
        return <PasswordReset />;
      }

      if (viewToRender === 'create-user') {
        return <CreateUser />;
      }

      if (viewToRender === 'forgot-password') {
        return <ForgotPassword />;
      }

      if (viewToRender === 'invite') {
        let title = '';
        const productName = params.product || params.productName;
        if (isInviteExpired()) {
          title = `Your email invitation to ${productName} has expired.`;
        } else if (isInviteCancelled()) {
          title = `Your email invitation to ${productName} has been revoked.`;
        }

        const message = (
          <span>
            Please contact your Admin:{' '}
            <a href={`mailto:${params.adminEmail}`} rel="noopener noreferrer" title="Admin Email">
              {params.adminEmail}
            </a>
            .
          </span>
        );

        return (
          <EmailNotification
            title={title}
            message={message}
            footerText={`Still Want Access to ${APPLICATION_NAME}? `}
            linkText="Create an Account"
            onClickLink={() => navigate('/create-user')}
          />
        );
      }

      if (isEmailVerificationPending()) {
        let title = '';
        let message;
        let error = false;
        switch (emailVerification) {
          case CloudActions.EMAIL_VERIFICATION_FETCHING:
            title = 'Waiting for data ...';
            break;
          case CloudActions.EMAIL_VERIFICATION_FAILED:
            title = 'Verification Failed';
            message =
              'If the token was expired or invalid, another email was sent. Check your inbox.';
            error = true;
            break;
          case CloudActions.EMAIL_VERIFICATION_UNVERIFIED:
            title = 'Email Verification Required';
            message = `An email has been sent to ${emailAddress} with a link and instructions on how to complete account verification.`;
            break;
          case CloudActions.EMAIL_VERIFICATION_EXPIRED:
            title = 'Email Verification Code Has Expired';
            message = `A new email has been sent to ${emailAddress} with a link and instructions on how to complete account verification.`;
            error = true;
            break;
          default:
            break;
        }

        return (
          <EmailNotification
            title={title}
            message={message}
            error={error}
            footerText="Can't find email? "
            linkText="Resend Email"
            onClickLink={() => resendEmailVerificationToken(userId, emailAddress)}
          />
        );
      }
    }

    // super-admin layout
    if (authenticated && isSuperAdmin()) {
      // change-password is a generic self-service action (its backend route requires only
      // `auth: 'jwt'`, no sudo/admin scope) — it must stay reachable regardless of the sudo
      // gate below, which is specifically about SuperAdmin-only functionality.
      if (viewToRender === 'change-password') {
        return <ChangePassword />;
      }

      // The SuperAdmin build is served from its own domain, but any authenticated user can
      // reach it — the `sudo` JWT claim (granted only to the backend's SuperAdmin allow-list,
      // see auth.ts's generateJWTForUserAsync) is what actually authorizes SuperAdmin access.
      // Without this gate, a regular authenticated user sees a rendered-but-broken UI, since
      // every SuperAdmin API call 403s once it reaches the backend.
      if (!AuthClientUtils.currentUserHasSudoClaim()) {
        return (
          <EmailNotification
            title="Not Authorized"
            message="You are not authorized to view Portal SuperAdmin."
            error
          />
        );
      }

      if (viewToRender === 'admin') {
        return <CloudAdmin store={store} />;
      }

      return <SuperAdmin store={store} />;
    }

    // authenticated portal layout
    if (authenticated) {
      if (viewToRender === 'change-password') {
        return <ChangePassword />;
      }

      if (viewToRender === 'admin') {
        return <CloudAdmin store={store} />;
      }

      if (viewToRender === 'seqgeq-trial' || viewToRender === 'product-trial') {
        const { productName } = params;
        if (productName && !chinaFlag()) {
          return <ProductTrial selectedProductName={productName} />;
        }
      }

      return (
        <React.Fragment>
          {viewToRender === 'manage-devices' && <ManageDevices />}
          {ENABLE__GDPR_FUTURE && viewToRender === 'settings' && <Settings />}
          {ENABLE__GDPR_FUTURE && viewToRender === 'port-data' && <PortPersonalData />}
          <UserAccount store={store} />
        </React.Fragment>
      );
    }

    return <SignIn />;
  };

  return (
    <FjFlexContainer data-test="portal">
      <AlertUi store={store} />
      {renderMain()}
    </FjFlexContainer>
  );
};

export default Router;
